import axios from "axios";

// Resilient backend URL — env var at build time first, current origin as fallback.
// This keeps the app working on custom domains even if REACT_APP_BACKEND_URL
// wasn't injected at build time (Emergent ingress routes /api to the backend
// regardless of which domain the page is served from).
const BACKEND =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");
export const API = `${BACKEND}/api`;

export const api = axios.create({ baseURL: API, timeout: 90000 });

api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("xseed_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export const auth = {
  login: (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data),
  register: (data) => api.post("/auth/register", data).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  deleteMe: () => api.delete("/me").then((r) => r.data),
};

export const dispensaries = {
  list: (params = {}) => api.get("/dispensaries", { params }).then((r) => r.data),
  get: (id) => api.get(`/dispensaries/${id}`).then((r) => r.data),
  create: (data) => api.post("/dispensaries", data).then((r) => r.data),
  remove: (id) => api.delete(`/dispensaries/${id}`).then((r) => r.data),
};

export const products = {
  list: (params = {}) => api.get("/products", { params }).then((r) => r.data),
  get: (id) => api.get(`/products/${id}`).then((r) => r.data),
  create: (data) => api.post("/products", data).then((r) => r.data),
  remove: (id) => api.delete(`/products/${id}`).then((r) => r.data),
};

export const deals = {
  list: (params = {}) => api.get("/deals", { params }).then((r) => r.data),
  create: (data) => api.post("/deals", data).then((r) => r.data),
  remove: (id) => api.delete(`/deals/${id}`).then((r) => r.data),
};

export const reviews = {
  list: (productId) => api.get(`/reviews/${productId}`).then((r) => r.data),
  create: (data) => api.post("/reviews", data).then((r) => r.data),
};

export const concierge = {
  chat: (data) => api.post("/concierge/chat", data).then((r) => r.data),
};

export const dashboardApi = {
  get: (dispId) => api.get(`/dashboard/${dispId}`).then((r) => r.data),
};

export const adminApi = {
  stats: () => api.get("/admin/stats").then((r) => r.data),
  reseed: (force = false) => api.post(`/admin/seed?force=${force}`).then((r) => r.data),
  clearSeed: () => api.delete("/admin/clear-seed").then((r) => r.data),
  // v2 control room
  overview: () => api.get("/admin/overview").then((r) => r.data),
  users: (params = {}) => api.get("/admin/users", { params }).then((r) => r.data),
  banUser: (id, reason = "") => api.post(`/admin/users/${id}/ban`, { reason }).then((r) => r.data),
  unbanUser: (id) => api.post(`/admin/users/${id}/unban`).then((r) => r.data),
  setRole: (id, role) => api.post(`/admin/users/${id}/role`, { role }).then((r) => r.data),
  resetStreak: (id) => api.post(`/admin/users/${id}/reset-streak`).then((r) => r.data),
  hardDeleteUser: (id) => api.delete(`/admin/users/${id}`).then((r) => r.data),
  posts: (status) => api.get("/admin/posts", { params: status ? { status } : {} }).then((r) => r.data),
  hidePost: (id, reason = "") => api.post(`/admin/posts/${id}/hide`, { reason }).then((r) => r.data),
  unhidePost: (id) => api.post(`/admin/posts/${id}/unhide`).then((r) => r.data),
  deletePost: (id) => api.delete(`/admin/posts/${id}`).then((r) => r.data),
  sessions: (status) => api.get("/admin/sessions", { params: status ? { status } : {} }).then((r) => r.data),
  killSession: (sid) => api.post(`/admin/sessions/${sid}/kill`).then((r) => r.data),
  wallets: () => api.get("/admin/wallets").then((r) => r.data),
  walletAdjust: (dispId, amount, note = "") => api.post(`/admin/wallets/${dispId}/adjust`, { amount, note }).then((r) => r.data),
  deals: () => api.get("/admin/deals").then((r) => r.data),
  featureDeal: (id) => api.post(`/admin/deals/${id}/feature`).then((r) => r.data),
  auditLog: (limit = 100) => api.get(`/admin/audit-log?limit=${limit}`).then((r) => r.data),
  // catalog CRUD
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data).then((r) => r.data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`).then((r) => r.data),
  updateDeal: (id, data) => api.put(`/admin/deals/${id}`, data).then((r) => r.data),
  deleteDeal: (id) => api.delete(`/admin/deals/${id}`).then((r) => r.data),
  updateDispensary: (id, data) => api.put(`/admin/dispensaries/${id}`, data).then((r) => r.data),
  // ML insights
  insights: () => api.get("/admin/insights").then((r) => r.data),
};

// ML / personalization
export const mlApi = {
  signal: (kind, target_type, target_id, meta = {}) =>
    api.post("/me/signal", { kind, target_type, target_id, meta }).then((r) => r.data).catch(() => null),
  taste: () => api.get("/me/taste").then((r) => r.data),
  recommendations: (state, limit = 12) =>
    api.get("/me/recommendations", { params: { state, limit } }).then((r) => r.data),
  trending: (state, limit = 8) =>
    api.get("/trending", { params: { state, limit } }).then((r) => r.data),
};

export const achievementsApi = {
  list: () => api.get("/achievements").then((r) => r.data),
};

export const reportsApi = {
  create: (target_type, target_id, reason = "") =>
    api.post("/reports", { target_type, target_id, reason }).then((r) => r.data),
  adminList: (status) => api.get("/admin/reports", { params: status ? { status } : {} }).then((r) => r.data),
  adminResolve: (id, action = "dismissed") =>
    api.post(`/admin/reports/${id}/resolve?action=${action}`).then((r) => r.data),
};

export const pushPrefsApi = {
  get: () => api.get("/push/prefs").then((r) => r.data),
  set: (prefs) => api.post("/push/prefs", prefs).then((r) => r.data),
};

export const claimsApi = {
  claim: (dealId, geo = {}) => api.post("/claims", { deal_id: dealId, ...geo }).then((r) => r.data),
  mine: () => api.get("/claims/me").then((r) => r.data),
  byCode: (code) => api.get(`/claims/code/${code}`).then((r) => r.data),
  redeem: (code, sale_amount, geo = {}) => api.post("/redeem", { code, sale_amount, ...geo }).then((r) => r.data),
};

export const walletApi = {
  state: (dispId) => api.get(`/wallet/${dispId}`).then((r) => r.data),
  deposit: (dispId, amount) => api.post(`/wallet/${dispId}/deposit`, { amount }).then((r) => r.data),
};

export const notifApi = {
  list: () => api.get("/notifications/me").then((r) => r.data),
  markRead: () => api.post("/notifications/mark-read").then((r) => r.data),
};

export const preorderApi = {
  create: (data) => api.post("/preorders", data).then((r) => r.data),
  mine: () => api.get("/preorders/me").then((r) => r.data),
};

export const waitlistApi = {
  join: (data) => api.post("/waitlist", data).then((r) => r.data),
  count: () => api.get("/waitlist/count").then((r) => r.data),
};

export const billingApi = {
  packages: () => api.get("/billing/packages").then((r) => r.data),
  checkout: (package_id, dispensary_id) => api.post("/billing/checkout", {
    package_id,
    origin_url: window.location.origin,
    dispensary_id: dispensary_id || null,
  }).then((r) => r.data),
  status: (session_id) => api.get(`/billing/status/${session_id}`).then((r) => r.data),
  cancelSubscription: (dispensary_id) => api.post("/billing/cancel-subscription", { dispensary_id }).then((r) => r.data),
  subscription: (dispensary_id) => api.get(`/billing/subscription/${dispensary_id}`).then((r) => r.data),
};

export const cartApi = {
  get: () => api.get("/cart").then((r) => r.data),
  add: (product_id, qty = 1) => api.post("/cart/add", { product_id, qty }).then((r) => r.data),
  update: (product_id, qty) => api.post("/cart/update", { product_id, qty }).then((r) => r.data),
  clear: () => api.delete("/cart/clear").then((r) => r.data),
  checkout: (mode, pickup_time, notes) =>
    api.post("/cart/checkout", { mode, pickup_time, notes, origin_url: window.location.origin }).then((r) => r.data),
};

export const postsApi = {
  list: (limit = 50) => api.get(`/posts?limit=${limit}`).then((r) => r.data),
  create: (data) => api.post("/posts", data).then((r) => r.data),
  like: (id) => api.post(`/posts/${id}/like`).then((r) => r.data),
  comment: (id, body) => api.post(`/posts/${id}/comments`, { body }).then((r) => r.data),
  remove: (id) => api.delete(`/posts/${id}`).then((r) => r.data),
};

export const leaderboardApi = {
  weekly: () => api.get("/leaderboard/weekly").then((r) => r.data),
  grantRewards: () => api.post("/leaderboard/grant-rewards").then((r) => r.data),
};

export const budVision = {
  analyze: (image_base64, state, note, personality) =>
    api.post("/bud/vision", { image_base64, state, note, personality }).then((r) => r.data),
};

export const budApi = {
  personalities: () => api.get("/bud/personalities").then((r) => r.data),
};

export const sessionsApi = {
  list: (state) => api.get(`/sessions${state ? `?state=${state}` : ""}`).then((r) => r.data),
  create: (data) => api.post("/sessions", data).then((r) => r.data),
  get: (sid, token) => api.get(`/sessions/${sid}${token ? `?token=${token}` : ""}`).then((r) => r.data),
  messages: (sid, since_ts) => api.get(`/sessions/${sid}/messages${since_ts ? `?since_ts=${since_ts}` : ""}`).then((r) => r.data),
  send: (sid, body) => api.post(`/sessions/${sid}/messages`, { body }).then((r) => r.data),
  close: (sid) => api.post(`/sessions/${sid}/close`).then((r) => r.data),
  tip: (sid, amount) => api.post(`/sessions/${sid}/tip`, { amount, origin_url: window.location.origin }).then((r) => r.data),
};
